import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Stack } from '@mui/material'

import { Footer } from 'components/Footer'
import { PageContainer } from 'components/UI/PageContainer'
import { PageHeader } from 'components/UI/PageHeader'
import { PAGE_GUTTER, PAGE_GUTTER_MOBILE } from 'constants/layout'
import { CertificatePageSkeleton } from 'components/pages/certificates/productPage/CertificatePageSkeleton'
import { useGetCertificatePublic } from 'hooks/api/useGetCertificatePublic'
import { usePushPage } from 'hooks/usePushPage'
import { useDeferredLoading } from 'hooks/useDeferredLoading'
import { ContentFade } from 'components/UI/ContentFade'
import { useSelector } from 'store/hooks'
import { isCertificateOwnedBy } from 'utils/certificateUtils';

/** Scroll-restore key for the certificate a sub-view was pushed from. */
export const certificateScrollKey = (certificateId?: string) => `certificate:${certificateId}`

interface CertificateSubViewProps {
  /** Page title — the row's label ("Provenance", "Scans", "Activity"). */
  title: string
  /** Sticky the footer attribution, for the infinitely-scrolling lists. */
  footerSticky?: boolean
  /**
   * The CONTENT reaches the bottom edge and owns where the attribution goes.
   *
   * For a screen whose content is a full-bleed surface — the scans map. The
   * shell renders no Footer at all in this mode: putting it above the whole
   * content lands it between the page title and the search bar, floating in the
   * header with nothing to belong to, so the content places it instead (see
   * ScansTabContent's `attribution` slot).
   */
  attributionAbove?: boolean
  /** Rendered with the loaded certificate. Not called until it has arrived. */
  children: (certificate: any) => React.ReactNode
}

/**
 * The shell every certificate sub-view sits in (VERA-315 Phase 4).
 *
 * Phase 4 replaced the certificate's four tabs with three chevron rows that push
 * to their own screens. This is what they push to: back-link + title, the
 * content, the footer.
 *
 * ## Why a route rather than an in-page overlay
 *
 * The push, the exit animation and the scroll restore already exist for
 * list → certificate (`utils/pageTransition`, `utils/scrollRestore`), and they
 * are route-shaped. An in-page overlay would have needed a second, parallel
 * navigation mechanism doing the same job — and `SharePage` had already set the
 * precedent that a certificate's sub-screen is a route.
 *
 * Note the app runs on a `MemoryRouter`, so these paths are not URLs anyone can
 * type or share. The route is a back-stack entry, not an address.
 *
 * ## Scroll
 *
 * Forward lands at the TOP: the sub-view shares no vertical structure with the
 * certificate, so arriving part-scrolled would be meaningless. Coming BACK
 * restores the certificate's own position — the row you tapped was probably
 * below the fold, and returning to the top of the page would lose your place.
 * The certificate page saves its position before pushing; this component only
 * has to not fight it.
 *
 * ## The owner check
 *
 * The rows that lead here render only in the owner view, and there is no URL to
 * type, so this is defence in depth rather than the access boundary — the API is
 * that. It lives here, once, instead of in each of the three pages.
 */
export const CertificateSubView: React.FC<CertificateSubViewProps> = ({
  title,
  footerSticky = false,
  attributionAbove = false,
  children,
}) => {
  const navigate = useNavigate()
  const { certificateId } = useParams()
  const { profile } = useSelector(({ auth }) => auth)

  const { data: certificate, isLoading } = useGetCertificatePublic(certificateId as string)

  /*
   * `isLoading && !certificate` is the SHELL's gate — it releases as soon as anything
   * is available, including the list cache's placeholder, which is why arriving from a
   * certificate is usually instant here. When it does fire it is the cold path (a
   * direct link, an NFC tap onto a sub-view), and there it was flashing like every
   * other surface. Deferred for the same reason and with the same numbers.
   */
  const showSkeleton = useDeferredLoading(isLoading && !certificate)

  const certificatePath = `/certificates/${certificateId}`

  /**
   * The shared push transition: in from the RIGHT, back out to the right, held
   * mounted for the exit before the route changes (`navigate()` unmounts on the
   * same tick, so an animation started at click time has nothing left to
   * animate). This replaces a hand-rolled `exiting` state + ref guard +
   * `setTimeout` — the third and last copy of that triple, after the certificate
   * page and SharePage.
   *
   * NOT `pushOnly`: the sender already sends. All three entries here go through
   * `CertificatePage`'s `openSubView`, which sets `slideIn` on the router state,
   * so the flag arrives correctly and there is nothing to bypass. `pushOnly`
   * exists for the case SharePage had — a caller that could not be edited (its
   * floating share disc) — and asserting it where a working flag already exists
   * would add a second mechanism for one job.
   *
   * `scrollToTop()` on mount comes with the hook and is not a behaviour change:
   * this component already did exactly that by hand, for the same reason (a
   * sub-view shares no vertical structure with the certificate, so an inherited
   * scroll offset is meaningless on it). Coming BACK is still untouched — the
   * certificate restores its own position from `scrollRestore`, which is what
   * returns you to the row you tapped.
   *
   * No router state on the way back, as before: threading the position through
   * the router would be a second channel for a thing that already has one.
   */
  /* `desktopPush` — the VERA-315 prototype. The certificate <-> sub-view pair is
     the one place in the app with unambiguous stack semantics, so it is where a
     desktop directional arrival is tested before going anywhere near the others.
     Entrance-only on desktop; see the option's own note. */
  const { pageSx, goBack } = usePushPage(certificatePath, { desktopPush: true })

  const isCertificateOwner = isCertificateOwnedBy(certificate, profile?.id)

  useEffect(() => {
    if (!certificate || isCertificateOwner) return
    navigate(certificatePath, { replace: true })
  }, [certificate, isCertificateOwner])

  if (showSkeleton) return <CertificatePageSkeleton />

  /* Inside the grace period. Nothing rather than a shell drawn against an absent
     certificate — the body below reads `certificate.*` throughout. */
  if (isLoading && !certificate) return null

  return (
    /* The platform fades the PAGE, not each section — `SupportRequestPage` and
       `WhatsNewAnnouncementPage` both wrap their own root, header included, because
       the whole page is what the skeleton stood in for. This is that wrap for all
       three sub-views (Provenance / Scans / Activity) at one site. Desktop only:
       `ContentFade` returns the child untouched below 900px, where the push already
       carries the navigation. */
    <ContentFade>
    <Stack
      sx={{
        ...pageSx,
        background: theme => theme.palette.background.default,
        /*
         * A full-bleed CONTENT page is exactly the viewport and does not scroll.
         *
         * `attributionAbove` means the content runs to the bottom edge — today
         * the Scans MAP, which is a fixed-height surface. It was sized with
         * `minHeight: 100vh` like every scrolling page, so the header and the
         * shell's padding pushed the total past the viewport and the page
         * scrolled a few dead pixels: no content down there, just the map
         * sliding up and back. `100dvh` (not `100vh`, which is the LARGE
         * viewport and overshoots while mobile browser chrome is showing) plus
         * `overflow: hidden` makes it exactly one screen.
         *
         * Everything else keeps `minHeight` — those pages are lists and are
         * meant to grow past the fold.
         */
        ...(attributionAbove
          ? { height: '100dvh', overflow: 'hidden' }
          : { minHeight: '100vh' }),
      }}
    >
      {/*
        THE SHARED COLUMN (`components/UI/PageContainer`), which these three
        screens were the loudest violation of.

        They had `p: 2` and no cap, so at a 1280 viewport Provenance ran
        16 → 1264 while the certificate it was one tap behind ran 104 → 1176 —
        160px apart, on screens the user moves between constantly. The container
        gives them the same `CONTENT_MAX_WIDTH` / `PAGE_GUTTER` the certificate page
        uses, so the two now share a column instead of each holding a private one.
        (It was `READING_MAX_WIDTH` until the two content widths collapsed into one
        on 2026-08-24 — the sharing is the point, not the particular number.)

        Vertical padding stays here rather than in the container: it is the one
        part that genuinely differs per screen. `pb: 0` when the content runs to
        the bottom edge — otherwise the shell's own padding sits under a map that
        is supposed to touch the frame.

        `xs` is unchanged at 16px on every side, which is `PAGE_GUTTER_MOBILE` and
        was `p: 2` before: the phone does not move.
      */}
      <PageContainer
        sx={{
          /*
           * NO TOP GUTTER. `PageHeader`'s own `space.lg` is the entire gap above
           * the back arrow, which is what puts that arrow exactly where the
           * certificate page's is — the page these screens are reached from.
           *
           * It used to add `PAGE_GUTTER_MOBILE`/`PAGE_GUTTER` (16/24) ON TOP of
           * the header's padding, so the arrow sat 21px lower than the one just
           * tapped (MEASURED at md: glyph top 45 against 24). Dropped at BOTH
           * breakpoints, not just desktop: the phone is the surface this app is
           * actually used on, so a parity fix that skipped it would fix the case
           * nobody sees and leave the one everybody does.
           *
           * The BOTTOM gutter stays — it is the page's own end padding and has
           * nothing to do with the header. It is set on its own line below
           * precisely so the two can no longer move together by accident, which
           * is how the top one got here.
           */
          pt: 0,
          pb: attributionAbove ? 0 : { xs: `${PAGE_GUTTER_MOBILE}px`, md: `${PAGE_GUTTER}px` },
          flex: '1 1 auto',
        }}
      >
        {/*
          STICKY here, unlike a bottom-nav destination.
          
          The nav is hidden on these screens, so this back-link is the ONLY way
          out — it cannot be allowed to scroll away on a list that runs to
          hundreds of rows. On the Activity TAB the opposite holds: the nav is
          present the whole time, so pinning a title there would be chrome
          duplicating chrome.
        */}
        <PageHeader
          sticky
          title={title}
          backLabel={certificate?.product?.title || 'Certificate'}
          onBack={goBack}
        />
        {/* VERA-315: on a screen whose content reaches the bottom of the viewport
            — the full-bleed scans map — the attribution moves ABOVE it. Below, it
            would either sit on top of the map or force the map to stop short of
            the frame, and the map touching the bottom edge is the whole point of
            that layout. Attribution is not content, so it is free to move; the
            map is the content, and it gets the edge. */}
        {certificate ? children(certificate) : null}
      </PageContainer>
      {!attributionAbove && (
        <Footer hidePoweredBy={certificate?.hide_powered_by} sticky={footerSticky} />
      )}
    </Stack>
    </ContentFade>
  )
}
